/**
 * Client-side image compression before upload.
 *
 * The server caps JPEG and PNG at 300 KB. Photographs taken on the phones this
 * programme's applicants actually own are 2-5 MB, so without this nearly every
 * upload fails with "File too large" and there is no way to fix it on the
 * device. Requirement C4.
 *
 * This is a convenience, not a security control — the server limit stays as
 * the backstop.
 */

export type CompressOptions = {
  /** Longest edge in pixels. Documents stay readable well below this. */
  maxEdge?: number;
  /** Target size in bytes. Kept under the server's 300 KB with headroom. */
  maxBytes?: number;
  /** Quality ladder, tried in order until one lands under maxBytes. */
  qualitySteps?: number[];
};

export type CompressResult = {
  file: File;
  previewUrl: string;
  originalBytes: number;
  compressedBytes: number;
  /** False when the input was left untouched — a PDF, or already small enough. */
  wasCompressed: boolean;
};

const DEFAULTS: Required<CompressOptions> = {
  maxEdge: 1600,
  maxBytes: 280 * 1024,
  qualitySteps: [0.8, 0.7, 0.6, 0.5, 0.4],
};

const COMPRESSIBLE = ["image/jpeg", "image/png", "image/webp"];

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not read that image. Try taking the photo again."));
    };
    image.src = url;
  });
}

function scaledSize(width: number, height: number, maxEdge: number) {
  const longest = Math.max(width, height);
  if (longest <= maxEdge) return { width, height };
  const ratio = maxEdge / longest;
  return {
    width: Math.round(width * ratio),
    height: Math.round(height * ratio),
  };
}

function toBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob | null> {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), "image/jpeg", quality);
  });
}

/**
 * Compresses an image to fit under the upload limit. Non-images (PDFs) and
 * images already small enough are returned unchanged, so callers can pass
 * every upload through this without branching.
 */
export async function compressImage(
  file: File,
  options: CompressOptions = {},
): Promise<CompressResult> {
  const { maxEdge, maxBytes, qualitySteps } = { ...DEFAULTS, ...options };

  const untouched = (): CompressResult => ({
    file,
    previewUrl: URL.createObjectURL(file),
    originalBytes: file.size,
    compressedBytes: file.size,
    wasCompressed: false,
  });

  if (!COMPRESSIBLE.includes(file.type)) return untouched();
  if (file.size <= maxBytes) return untouched();

  const image = await loadImage(file);
  const { width, height } = scaledSize(image.width, image.height, maxEdge);

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext("2d");
  if (!context) return untouched();

  // A white ground matters: PNGs with transparency turn black in JPEG.
  context.fillStyle = "#ffffff";
  context.fillRect(0, 0, width, height);
  context.drawImage(image, 0, 0, width, height);

  let chosen: Blob | null = null;
  for (const quality of qualitySteps) {
    const blob = await toBlob(canvas, quality);
    if (!blob) continue;
    chosen = blob;
    if (blob.size <= maxBytes) break;
  }

  // Every quality step still too large, or the encoder produced nothing:
  // hand back the original and let the server reject it with its own message
  // rather than uploading something worse than what we started with.
  if (!chosen || chosen.size >= file.size) return untouched();

  const renamed = file.name.replace(/\.[^.]+$/, "") || "upload";
  const compressed = new File([chosen], `${renamed}.jpg`, {
    type: "image/jpeg",
    lastModified: Date.now(),
  });

  return {
    file: compressed,
    previewUrl: URL.createObjectURL(compressed),
    originalBytes: file.size,
    compressedBytes: compressed.size,
    wasCompressed: true,
  };
}

/** "2.4 MB", "287 KB" — for telling the applicant what will be sent. */
export function formatBytes(bytes: number) {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.round(bytes / 1024)} KB`;
}
